/**
 * A run's status and progress, kept live: polls the run until it is terminal
 * (`useSolutionRun`) and reports each fresh copy to the parent, so a page can
 * render the result the moment it lands.
 */

import { useEffect, useRef } from "react";
import { Alert, Badge, Group, Loader, Paper, Progress, Stack, Text } from "@mantine/core";
import { isTerminal, type RunDetailOut } from "@/shared/api/solutionRuns";
import { RUN_STATUS_COLOR, RUN_STATUS_LABEL, progressPercent } from "./runMeta";
import { useSolutionRun } from "./useSolutionRun";

interface Props {
  runId: string;
  /** Every fresh copy of the run (each poll). */
  onUpdate?: (run: RunDetailOut) => void;
  /** Once, when the run reaches `succeeded` or `failed`. */
  onFinished?: (run: RunDetailOut) => void;
}

export function RunStatus({ runId, onUpdate, onFinished }: Props) {
  const { run, loading, error } = useSolutionRun(runId);
  const finishedFor = useRef<string | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const scrolledFor = useRef<string | null>(null);

  // On a narrow screen the status panel sits below the run list; bring a newly
  // picked run into view so selecting one visibly does something.
  useEffect(() => {
    const el = panel.current;
    if (!run || !el || scrolledFor.current === run.run_id) return;
    scrolledFor.current = run.run_id;
    const box = el.getBoundingClientRect();
    if (box.top < 0 || box.bottom > window.innerHeight) {
      el.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  }, [run]);

  useEffect(() => {
    if (!run) return;
    onUpdate?.(run);
    if (isTerminal(run.status) && finishedFor.current !== run.run_id) {
      finishedFor.current = run.run_id;
      onFinished?.(run);
    }
  }, [run, onUpdate, onFinished]);

  if (loading && !run) return <Loader size="sm" aria-label="Loading run" />;
  if (error && !run) {
    return (
      <Alert color="red" title="Could not load the run">
        {error}
      </Alert>
    );
  }
  if (!run) return null;

  const pct = progressPercent(run.progress);
  const active = !isTerminal(run.status);
  return (
    <Paper ref={panel} withBorder radius="md" p="md" data-testid="run-status">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Badge color={RUN_STATUS_COLOR[run.status]} variant="light">
              {RUN_STATUS_LABEL[run.status]}
            </Badge>
            {run.status === "queued" && (
              <Text size="sm" c="dimmed">
                Waiting for a runner…
              </Text>
            )}
          </Group>
          {run.progress.total > 0 && (
            <Text size="sm" aria-label="Run progress count">
              {run.progress.done} / {run.progress.total}
            </Text>
          )}
        </Group>
        {(active || pct !== null) && (
          <Progress
            value={pct ?? 100}
            animated={active}
            striped={active || pct === null}
            color={run.status === "failed" ? "red" : undefined}
            aria-label="Run progress"
          />
        )}
        {run.progress.label && active && (
          <Text size="xs" c="dimmed" truncate>
            {run.progress.label}
          </Text>
        )}
        {run.status === "failed" && run.error && (
          <Alert color="red" variant="light" title="The run failed">
            {run.error}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
